import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { store } from "Store/mainStore";

/**
 * Standalone Datamart v3 service.
 *
 * Uses a dedicated axios instance so its request interceptor
 * (Bearer token, idToken, x-user-id) is completely isolated from
 * every other service / axios usage in the codebase.
 */
class DatamartV3Services {
  private readonly http: AxiosInstance;

  constructor() {
    this.http = axios.create({
      baseURL: process.env.REACT_APP_DATAMART,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.http.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const reduxStore = store.getState();
        const accessToken = reduxStore?.authSlice?.login?.adToken?.accessToken;
        const idToken = reduxStore?.authSlice?.login?.adToken?.idToken;
        const userId = reduxStore?.authSlice?.login?.id ?? "";

        if (accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
        if (idToken) {
          config.headers["idToken"] = idToken;
        }
        config.headers["x-user-id"] = userId;

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Fetch contract deviation records from the v3 API.
   *
   * Endpoint:
   *   GET /v3/contract/deviation
   *     ?properties=id,section_title,standard_term,type,type_info,display_order
   *     &sort=display_order:asc
   */
  async fetchContractDeviations() {
    try {
      const res = await this.http.get("/v3/contract/deviation", {
        params: {
          properties: "id,section_title,standard_term,type,type_info,display_order",
          sort: "display_order:asc",
        },
      });

      return {
        status: true,
        statusCode: res.status,
        data: res.data?.data ?? res.data ?? [],
        total_count: res.data?.total_count ?? 0,
        message: res.data?.message ?? "",
      };
    } catch (error: any) {
      console.error("[DatamartV3Services] fetchContractDeviations error:", error);
      return {
        status: false,
        statusCode: error?.response?.status ?? null,
        data: [],
        total_count: 0,
        message: error?.response?.data?.errorMessage ?? error?.message ?? "Unknown error",
      };
    }
  }

  /**
   * Add agreement deviation details to the v3 API.
   *
   * Endpoint:
   *   PUT /v3/contract/deviation - For upserting agreement deviation details
   */
  async addAgreementDeviationDetails(details: any) {
    try {
      const res = await this.http.put("/v3/contract", { ...details });
      return {
        status: true,
        statusCode: res.status,
        data: res.data?.data ?? res.data ?? [],
        message: res.data?.message ?? "",
      };
    } catch (error: any) {
      console.error("[DatamartV3Services] addAgreementDeviationDetails error:", error);
      return {
        status: false,
        statusCode: error?.response?.status ?? null,
        data: [],
        message: error?.response?.data?.errorMessage ?? error?.message ?? "Unknown error",
      };
    }
  }
}

export const datamartV3Services = new DatamartV3Services();
export default datamartV3Services;
